import React, { useState, useEffect } from 'react';
import { Drawer, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Collapse, IconButton } from '@mui/material';
import { Home, Storage, Code, Folder, ContactMail, Info, Logout, Add, Remove, Description, DataArray, AddCircleOutline, Person, Business, Login } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const drawerWidth = 260;

const LeftSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setSelectedProjectId, selectedProjectId } = useAuth();
  const [projects, setProjects] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get('/projects/recent');
        setProjects(res.data);
      } catch (err) {
        if (err.response?.status !== 401) {
          console.error(err);
        }
        // Don't retry on 401 - interceptor handles redirect
      }
    };
    fetchProjects();
  }, [user]);

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  const handleProjectClick = (projectId) => {
    setSelectedProjectId(projectId);
  };

  const isActive = (path) => location.pathname === path;
  
  const menuItemStyle = {
    borderRadius: 2, mx: 1, mb: 0.5,
    '&.Mui-selected': { bgcolor: '#1abc9c', color: 'white', '&:hover': { bgcolor: '#16a085' } },
    '&:hover': { bgcolor: '#34495e' },
  };

  const subMenuItemStyle = {
    borderRadius: 2, mx: 1, mb: 0.5, pl: 6,
    '&:hover': { bgcolor: '#34495e' },
  };

  return (
    <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', background: '#2c3e50', color: 'white', borderRight: 'none' } }}>
      <Box sx={{ bgcolor: '#1abc9c', py: 2.5, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={600}>P&C Insurance Pricing</Typography>
      </Box>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="body2">{user?.email || 'Guest'}</Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>{user ? `Role: ${user.role}` : 'Not signed in'}</Typography>
      </Box>
      <List sx={{ p: 1 }}>
        <ListItem disablePadding><ListItemButton onClick={() => { setSelectedProjectId(null); navigate('/'); }} selected={isActive('/')} sx={menuItemStyle}>
          <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Home /></ListItemIcon><ListItemText primary="Home" />
        </ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => { setSelectedProjectId(null); navigate('/data'); }} selected={isActive('/data')} sx={menuItemStyle}>
          <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Storage /></ListItemIcon><ListItemText primary="Import & Data" />
        </ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => { setSelectedProjectId(null); navigate('/python-playground'); }} selected={isActive('/python-playground')} sx={menuItemStyle}>
          <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Code /></ListItemIcon><ListItemText primary="Python Playground" />
        </ListItemButton></ListItem>
        <Divider sx={{ my: 1, bgcolor: '#465668' }} />
        
        {/* PROJECTS Header with + button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
          <Typography variant="overline" sx={{ color: '#9aa9b9' }}>PROJECTS</Typography>
          {user && (
            <IconButton size="small" onClick={() => navigate('/projects/new')} sx={{ color: '#1abc9c', p: 0.5, '&:hover': { bgcolor: 'rgba(26,188,156,0.15)' } }}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          )}
        </Box>
        
        {/* Recent Projects (max 5) */}
        {projects.slice(0, 5).map(proj => (
          <Box key={proj.id}>
            <ListItem disablePadding>
              <ListItemButton onClick={() => handleProjectClick(proj.id)} selected={selectedProjectId === proj.id} sx={menuItemStyle}>
                <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Folder /></ListItemIcon>
                <ListItemText primary={proj.name} primaryTypographyProps={{ noWrap: true, fontSize: '13px', style: { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleProjectExpand(proj.id); }} sx={{ color: 'white' }}>
                  {expandedProjects[proj.id] ? <Remove fontSize="small" /> : <Add fontSize="small" />}
                </IconButton>
              </ListItemButton>
            </ListItem>
            <Collapse in={expandedProjects[proj.id]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setSelectedProjectId(null); navigate(`/projects/${proj.id}/analyses`); }} sx={subMenuItemStyle}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Description fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Analyses" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { setSelectedProjectId(null); navigate(`/projects/${proj.id}/datasets`); }} sx={subMenuItemStyle}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><DataArray fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Datasets" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </Box>
        ))}
        
        {/* See All Projects link */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/projects')} sx={{ ...menuItemStyle, color: '#1abc9c' }}>
            <ListItemIcon sx={{ color: '#1abc9c', minWidth: 35 }}><Folder /></ListItemIcon>
            <ListItemText primary="See All Projects →" primaryTypographyProps={{ fontSize: '12px' }} />
          </ListItemButton>
        </ListItem>
        
        <Divider sx={{ my: 1, bgcolor: '#465668' }} />
        {user?.role === 'enterprise_admin' && (
          <ListItem disablePadding><ListItemButton onClick={() => navigate('/users')} selected={isActive('/users')} sx={menuItemStyle}>
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Person /></ListItemIcon><ListItemText primary="User Management" />
          </ListItemButton></ListItem>
        )}
        {user?.role === 'superuser' && (
          <>
            <ListItem disablePadding><ListItemButton onClick={() => navigate('/users')} selected={isActive('/users')} sx={menuItemStyle}>
              <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Person /></ListItemIcon><ListItemText primary="User Management" />
            </ListItemButton></ListItem>
            <ListItem disablePadding><ListItemButton onClick={() => navigate('/enterprises')} selected={isActive('/enterprises')} sx={menuItemStyle}>
              <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Business /></ListItemIcon><ListItemText primary="Enterprises" />
            </ListItemButton></ListItem>
          </>
        )}
        <ListItem disablePadding><ListItemButton onClick={() => navigate('/contact')} selected={isActive('/contact')} sx={menuItemStyle}>
          <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><ContactMail /></ListItemIcon><ListItemText primary="Contact" />
        </ListItemButton></ListItem>
        <ListItem disablePadding><ListItemButton onClick={() => navigate('/about')} selected={isActive('/about')} sx={menuItemStyle}>
          <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Info /></ListItemIcon><ListItemText primary="About" />
        </ListItemButton></ListItem>
        {user ? (
          <ListItem disablePadding><ListItemButton onClick={() => { logout(); navigate('/'); }} sx={menuItemStyle}>
            <ListItemIcon sx={{ color: 'white', minWidth: 35 }}><Logout /></ListItemIcon><ListItemText primary="Logout" />
          </ListItemButton></ListItem>
        ) : (
          <ListItem disablePadding><ListItemButton onClick={() => navigate('/login')} sx={menuItemStyle}>
            <ListItemIcon sx={{ color: '#1abc9c', minWidth: 35 }}><Login /></ListItemIcon><ListItemText primary="Sign In" />
          </ListItemButton></ListItem>
        )}
      </List>
    </Drawer>
  );
};

export default LeftSidebar;